import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useGameLogic } from './application/useGameLogic';
import { StartScreen } from './view/screens/StartScreen';
import { GameOverScreen } from './view/screens/GameOverScreen';
import { EndOfSeasonScreen } from './view/screens/EndOfSeasonScreen';
import { SackedScreen } from './view/screens/SackedScreen';
import { SimulationOverlay } from './view/overlays/SimulationOverlay';
import { SeasonAdvancementOverlay } from './view/overlays/SeasonAdvancementOverlay';
import { ChallengesScreen } from './view/screens/ChallengesScreen';
import { AppRouter } from './view/routing/AppRouter';
import { useTheme } from './hooks/useTheme';
import * as Sentry from "@sentry/react";
import { DataService } from './services/DataService';
import { FixturesHelper } from './services/FixturesHelper';
import { GAME_VERSION_NUMBER } from './version';
import { GameActionsProvider, GameActions, useGameActions } from './contexts/GameActionsContext';
import { UIProvider, useUI } from './contexts/UIContext';
import { MainLayout } from './view/layout/MainLayout';
import { GameState, MatchEvent, Result } from './domain/entities';
import { FeedbackModal } from './view/shared/FeedbackModal';
import { IncomingBidsModal } from './view/modals/IncomingBidsModal';

interface AppContentProps {
    gameState: GameState | { gameStarted: false };
    isLoading: boolean;
    isSimulating: boolean;
    simulationProgress: {
        title: string;
        playerMatchEvents: MatchEvent[] | null;
        result: Result | null;
        isPlayerMatch?: boolean;
    };
    sackedInfo: any | null;
    hasSave: boolean;
    migrationError: string | null;
}

export interface View {
    type: string;
    title: string;
    [key: string]: any; // for props like teamName, playerId, activeTab, filters
}

export const AppContent: React.FC<AppContentProps> = ({
    gameState,
    isLoading,
    isSimulating,
    simulationProgress,
    sackedInfo,
    hasSave,
    migrationError
}) => {
    const gameActions = useGameActions();
    const uiState = useUI();

    const [history, setHistory] = useState<View[]>([{ type: 'play', title: 'Dashboard' }]);
    const [historyIndex, setHistoryIndex] = useState(0);
    const mainContentRef = useRef<HTMLElement>(null);
    const [appView, setAppView] = useState<'game' | 'start' | 'challenges'>('start');
    const [loadError, setLoadError] = useState<Error | null>(null);
    const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
    const [isIncomingBidsModalOpen, setIsIncomingBidsModalOpen] = useState(false);
    const [showEndOfSeasonScreen, setShowEndOfSeasonScreen] = useState(false);
    const [isShowingSeasonAdvancement, setIsShowingSeasonAdvancement] = useState(false);
    const isHandlingPopState = useRef(false);

    const { showClickAt, showDebug, toggleMobileNav, isAiPunditEnabled, isMobileNavOpen } = useUI();
    const { playWeek, updateTactics, promoteYouthPlayer, sellPlayer, negotiatePurchase, finalizePurchase, restartWithTeamCount: originalRestartWithTeamCount } = gameActions;

    // Wrap restartWithTeamCount to also reset navigation
    const restartWithTeamCountAndNav = useCallback(async (teamCount: number) => {
        await originalRestartWithTeamCount(teamCount);
        setHistory([{ type: 'play', title: 'Dashboard' }]);
        setHistoryIndex(0);
    }, [originalRestartWithTeamCount]);

    const currentView = history[historyIndex];
    useTheme(gameState, currentView);

    useEffect(() => {
        // Timeout for loading process
        if (!isLoading) return;

        const timeoutId = setTimeout(() => {
            const error = new Error("Game loading timed out after 10 seconds.");
            Sentry.captureException(error);
            setLoadError(error);
        }, 10000); // 10 seconds

        return () => clearTimeout(timeoutId);
    }, [isLoading]);

    useEffect(() => {
        // Validate game state after loading finishes
        if (!isLoading && gameState.gameStarted && !loadError) {
            let errorMessage: string | null = null;

            if (!gameState.config) {
                errorMessage = `Invalid game state after load: gameState.config is missing.`;
            } else {
                const playerLeague = gameState.leagues[gameState.playerLeagueName];
                if (!playerLeague) {
                    errorMessage = `Invalid game state after load: playerLeague '${gameState.playerLeagueName}' not found.`;
                } else {
                    const playerTeam = playerLeague.teams.find(t => t.name === gameState.playerTeamName);
                    if (!playerTeam) {
                        errorMessage = `Invalid game state after load: playerTeam '${gameState.playerTeamName}' not found in league '${gameState.playerLeagueName}'.`;
                    }
                }
            }
            
            if (errorMessage) {
                const error = new Error(errorMessage);
                Sentry.captureException(error, {
                    extra: { gameStateSnapshot: JSON.stringify(gameState).substring(0, 4000) }
                });
                setLoadError(error);
            }
        }
    }, [isLoading, gameState, loadError]);

    useEffect(() => {
        if (sackedInfo) {
            window.scrollTo(0, 0);
        }
    }, [sackedInfo]);

    useEffect(() => {
        if (gameState.gameStarted) {
            setAppView('game');
        } else if (appView === 'game') {
            setAppView('start');
        }
    }, [gameState.gameStarted, appView]);

    // Reset showEndOfSeasonScreen when season changes
    useEffect(() => {
        if (!gameState.gameStarted) return;
        setShowEndOfSeasonScreen(false);
    }, [gameState.gameStarted, (gameState.gameStarted ? gameState.season : null)]);

    // Handle season advancement while overlay is visible
    useEffect(() => {
        if (!isShowingSeasonAdvancement) return;

        let timer: NodeJS.Timeout | undefined;

        // Wait for browser to paint overlay, then schedule heavy work
        // First rAF: ensures paint happens
        // setTimeout: defers work to after paint is complete
        const raf = requestAnimationFrame(() => {
            timer = setTimeout(() => {
                gameActions.advanceSeason();
                setTimeout(() => setIsShowingSeasonAdvancement(false), 5500);
            }, 0);
        });

        return () => {
            cancelAnimationFrame(raf);
            if (timer) clearTimeout(timer);
        };
    }, [isShowingSeasonAdvancement, gameActions]);

    // Initialize browser history with current view
    useEffect(() => {
        if (!gameState.gameStarted) return;

        // Replace initial state with our view data
        window.history.replaceState(
            { view: currentView, historyIndex: 0 },
            '',
            window.location.pathname
        );
    }, [gameState.gameStarted]); // Only run once when game starts

    // Sync game history with browser history
    useEffect(() => {
        if (!gameState.gameStarted || isHandlingPopState.current) return;

        // Push to browser history when navigating forward in game
        window.history.pushState(
            { view: currentView, historyIndex },
            '',
            window.location.pathname
        );
    }, [historyIndex, currentView, gameState.gameStarted]);

    // Handle browser back/forward buttons
    useEffect(() => {
        if (!gameState.gameStarted) return;

        const handlePopState = (event: PopStateEvent) => {
            if (event.state && event.state.view) {
                isHandlingPopState.current = true;

                const targetIndex = event.state.historyIndex;

                // Update to the browser history state
                if (targetIndex >= 0 && targetIndex < history.length) {
                    setHistoryIndex(targetIndex);
                }

                // Reset flag after state update
                setTimeout(() => {
                    isHandlingPopState.current = false;
                }, 0);
            }
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [history, gameState.gameStarted]);
    
    const handleStartNewGame = (data: any | null, gameType: 'mens' | 'womens' = 'mens') => {
        gameActions.startNewGame(data, gameType);
        setHistory([{ type: 'play', title: 'Dashboard' }]);
        setHistoryIndex(0);
    };

    const handleStartChallenge = (type: 'great_escape' | 'youth_academy' | 'debt_crisis', gameType: 'mens' | 'womens') => {
        gameActions.startChallengeGame(type, gameType);
        setHistory([{ type: 'play', title: 'Dashboard' }]);
        setHistoryIndex(0);
    };

    const navigate = useCallback((view: View) => {
        setHistory([view]);
        setHistoryIndex(0);
        if (isMobileNavOpen) {
            toggleMobileNav();
        }
        if (mainContentRef.current) {
            mainContentRef.current.scrollTo(0, 0);
        }
    }, [toggleMobileNav, isMobileNavOpen]);

    const pushView = (view: View) => {
        const newHistory = history.slice(0, historyIndex + 1);
        newHistory.push(view);
        setHistory(newHistory);
        setHistoryIndex(newHistory.length - 1);
        if (mainContentRef.current) {
            mainContentRef.current.scrollTo(0, 0);
        }
    };

    const goBack = () => {
        if (historyIndex > 0) {
            setHistoryIndex(historyIndex - 1);
        }
    };

    const goForward = () => {
        if (historyIndex < history.length - 1) {
            setHistoryIndex(historyIndex - 1);
        }
    };

    const updateCurrentView = (propsToUpdate: Partial<View>) => {
        setHistory(prevHistory => {
            const newHistory = [...prevHistory];
            const current = { ...newHistory[historyIndex] };
            newHistory[historyIndex] = { ...current, ...propsToUpdate };
            return newHistory;
        });
    };

    if (isLoading) {
        return <div className="loading-screen"><h1>Loading Game...</h1></div>;
    }
    
    if (loadError) {
        return (
            <div className="game-over-screen">
                <h1>Load Error 😵</h1>
                <p>Sorry, there was a problem loading your game. The saved data might be corrupted or from an incompatible version.</p>
                <p>To help us fix this, you can export your game data and report the issue. Alternatively, reset all data to start fresh.</p>
                 <p className="text-secondary" style={{ fontSize: '0.9em', marginTop: '16px', maxWidth: '600px', wordBreak: 'break-word', lineHeight: 1.4 }}>
                    Error details: {loadError.message}
                </p>
                <div style={{ display: 'flex', gap: '16px', marginTop: '24px', flexWrap: 'wrap', justifyContent: 'center' }}>
                    <button 
                        className="button" 
                        onClick={() => {
                            if (gameState.gameStarted) {
                                DataService.exportFullGameState(gameState);
                            }
                        }}
                        disabled={!gameState.gameStarted}
                    >
                        Export Game Data
                    </button>
                    <button className="button danger" onClick={gameActions.hardResetGame}>Reset All Data</button>
                </div>
                <p style={{
                    position: 'absolute',
                    bottom: '10px',
                    right: '10px',
                    color: '#888',
                    fontSize: '0.9em',
                    opacity: 0.8
                }}>
                    v{GAME_VERSION_NUMBER}
                </p>
            </div>
        );
    }

    if (!gameState.gameStarted) {
        if (appView === 'challenges') {
            return <ChallengesScreen onStartChallenge={handleStartChallenge} onBack={() => setAppView('start')} />;
        }
        return <StartScreen
            onStart={gameActions.startGame}
            onNewGame={handleStartNewGame}
            hasSave={hasSave}
            onLoadGameState={gameActions.loadFullGameState}
            listSaves={gameActions.listSaves}
            onLoadNamedGame={gameActions.loadNamedGame}
            onDeleteNamedGame={gameActions.deleteNamedGame}
            onShowChallenges={() => setAppView('challenges')}
            migrationError={migrationError}
            adsEnabled={uiState.adsEnabled}
        />;
    }

    if (gameState.gameOver) {
        return <GameOverScreen onRestart={gameActions.restartGame} />;
    }
    
    // Check if all league and cup fixtures have been completed
    // Find the maximum week number across all leagues and cups
    const maxWeek = FixturesHelper.getMaxWeek(gameState.fixtures);
    const isEndOfSeason = maxWeek > 0 && gameState.currentWeek > maxWeek;

    if (sackedInfo) {
        return <SackedScreen sackedInfo={sackedInfo} onContinue={(info) => gameActions.continueAfterSacking(info)} gameState={gameState} />;
    }

    if (isEndOfSeason && showEndOfSeasonScreen) {
        return <EndOfSeasonScreen gameState={gameState} onAdvance={() => {
            setIsShowingSeasonAdvancement(true);
            setShowEndOfSeasonScreen(false);
            // Heavy work happens in useEffect while overlay is visible
        }} isAiPunditEnabled={isAiPunditEnabled} />;
    }
    
    return (
        <>
            {isSimulating && gameState.gameStarted && (
                <SimulationOverlay
                    gameState={gameState}
                    title={simulationProgress.title}
                    playerMatchEvents={simulationProgress.playerMatchEvents}
                    result={simulationProgress.result}
                    isPlayerMatch={simulationProgress.isPlayerMatch}
                    onDismiss={gameActions.dismissSimulation}
                    onCancel={() => {
                        gameActions.dismissSimulation(false);
                        navigate({ type: 'tactics', title: 'Tactics' });
                    }}
                    onMatchStart={gameActions.commitPendingState}
                    adsEnabled={true}
                />
            )}
            {isShowingSeasonAdvancement && gameState.gameStarted && (
                <SeasonAdvancementOverlay isVisible={isShowingSeasonAdvancement} />
            )}
            <MainLayout
                mainContentRef={mainContentRef}
                gameState={gameState}
                currentView={currentView}
                historyIndex={historyIndex}
                historyLength={history.length}
                goBack={goBack}
                goForward={goForward}
                navigate={navigate}
                showDebug={showDebug}
                onOpenBidsModal={() => setIsIncomingBidsModalOpen(true)}
            >
                <AppRouter
                    gameState={gameState}
                    currentView={currentView}
                    pushView={pushView}
                    updateCurrentView={updateCurrentView}
                    isSimulating={isSimulating}
                    onRestartWithTeamCount={restartWithTeamCountAndNav}
                    onGoToEndOfSeason={() => setShowEndOfSeasonScreen(true)}
                />
            </MainLayout>
            <FeedbackModal isOpen={isFeedbackModalOpen} onClose={() => setIsFeedbackModalOpen(false)} />
            {gameState.gameStarted && (
                <IncomingBidsModal
                    isOpen={isIncomingBidsModalOpen}
                    onClose={() => setIsIncomingBidsModalOpen(false)}
                    gameState={gameState}
                    onAcceptBid={gameActions.acceptIncomingBid}
                    onRejectBid={gameActions.rejectIncomingBid}
                    onNegotiateBid={gameActions.negotiateIncomingBid}
                    onRespondToNegotiation={gameActions.respondToIncomingBidNegotiation}
                />
            )}
        </>
    );
};

export const App = () => {
    // This is now the top-level state and action provider component.
    const gameLogicProps = useGameLogic();

    const {
        startGame, startNewGame, startChallengeGame, resetAndGoToStart, playWeek, advanceSeason, continueAfterSacking, updateTactics,
        updateTeamTrainingFocus, updatePositionalTrainingFocus, updatePlayerTrainingFocus, upgradeTrainingFacilities,
        downgradeTrainingFacilities, renewContract, restartGame, hardResetGame, negotiatePurchase, finalizePurchase,
        sellPlayer, unlistPlayer, releasePlayer, upgradeYouthAcademy, downgradeYouthAcademy, promoteYouthPlayer, updateYouthPreferences, loadFullGameState,
        listSaves, loadNamedGame, saveNamedGame, deleteNamedGame, markNewsAsRead, simulateFullSeason, simulateFourWeeks,
        simulateToCupFinal, simulateToSeasonWin,
        addPlayerMoney, setDebugFlag, toggleDebugFlag, updateManagerName, handlePlayerConversation,
        offerFinancialIncentive, cacheAiPunditTake, cacheAiMatchReport, cacheAiPlayerRoleAnalysis, dismissSimulation,
        addElitePlayer, addLegendPlayer, generateGreatYouthIntake, boostTeamMorale, healAllInjuries, restartWithTeamCount, commitPendingState,
        acceptIncomingBid, rejectIncomingBid, negotiateIncomingBid, respondToIncomingBidNegotiation, updateSearchHistory, submitFreeAgentBid,
        acceptTransferSuggestion, rejectTransferSuggestion, adjustListingPrice
    } = gameLogicProps;

    const gameActions: GameActions = {
        startGame, startNewGame, startChallengeGame, resetAndGoToStart, playWeek, advanceSeason, continueAfterSacking, updateTactics,
        updateTeamTrainingFocus, updatePositionalTrainingFocus, updatePlayerTrainingFocus, upgradeTrainingFacilities,
        downgradeTrainingFacilities, renewContract, restartGame, hardResetGame, negotiatePurchase, finalizePurchase,
        sellPlayer, unlistPlayer, releasePlayer, upgradeYouthAcademy, downgradeYouthAcademy, promoteYouthPlayer, updateYouthPreferences, loadFullGameState,
        listSaves, loadNamedGame, saveNamedGame, deleteNamedGame, markNewsAsRead, simulateFullSeason, simulateFourWeeks,
        simulateToCupFinal, simulateToSeasonWin,
        addPlayerMoney, setDebugFlag, toggleDebugFlag, updateManagerName, handlePlayerConversation,
        offerFinancialIncentive, cacheAiPunditTake, cacheAiMatchReport, cacheAiPlayerRoleAnalysis, dismissSimulation,
        addElitePlayer, addLegendPlayer, generateGreatYouthIntake, boostTeamMorale, healAllInjuries, restartWithTeamCount, commitPendingState,
        acceptIncomingBid, rejectIncomingBid, negotiateIncomingBid, respondToIncomingBidNegotiation, updateSearchHistory, submitFreeAgentBid,
        acceptTransferSuggestion, rejectTransferSuggestion, adjustListingPrice
    };

    return (
        <GameActionsProvider actions={gameActions}>
            <UIProvider>
                <AppContent
                    gameState={gameLogicProps.gameState}
                    isLoading={gameLogicProps.isLoading}
                    isSimulating={gameLogicProps.isSimulating}
                    simulationProgress={gameLogicProps.simulationProgress}
                    sackedInfo={gameLogicProps.sackedInfo}
                    hasSave={gameLogicProps.hasSave}
                    migrationError={gameLogicProps.loadError}
                />
            </UIProvider>
        </GameActionsProvider>
    );
}
